
import { Assessment, VersionHistoryEntry } from '@/types/assessment';
import { createServiceClient } from '@/services/migration/serviceClient';
import { getAssessmentById } from './getAssessments';
import { saveCategoriesQuestionsAnswers } from './saveCategoriesStructure';

// Helper function to check if content-related changes were made
export const hasContentChanges = (original: Assessment, updates: Partial<Assessment>): boolean => {
  // If categories are being updated, it's a content change
  if (updates.categories) return true;
  
  return false;
};

// Helper function to create a version history entry
export const createVersionHistoryEntry = (assessment: Assessment): VersionHistoryEntry => {
  return {
    versionNumber: assessment.version,
    timestamp: new Date().toISOString(),
    categories: JSON.parse(JSON.stringify(assessment.categories)) // Deep clone categories
  };
};

// Publish an assessment
export const publishAssessment = async (id: string): Promise<Assessment | null> => {
  try {
    // Use service client to bypass RLS policies
    const serviceClient = createServiceClient();
    
    const currentAssessment = await getAssessmentById(id);
    if (!currentAssessment) {
      return null;
    }
    
    // Can only publish a draft
    if (currentAssessment.status !== 'draft') {
      return currentAssessment;
    }
    
    // Set status to published and version to 1
    const { error } = await serviceClient
      .from('assessments')
      .update({
        status: 'published',
        version: 1,
        is_active: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);
      
    if (error) {
      console.error('Error publishing assessment:', error);
      throw error;
    }
    
    // Get the updated assessment
    return await getAssessmentById(id);
  } catch (error) {
    console.error('Error in publishAssessment:', error);
    throw error;
  }
};

// Revert to a specific version
export const revertToVersion = async (id: string, versionNumber: number): Promise<Assessment | null> => {
  try {
    // Use service client to bypass RLS policies
    const serviceClient = createServiceClient();
    
    // Get the current assessment
    const currentAssessment = await getAssessmentById(id);
    if (!currentAssessment) {
      return null;
    }
    
    // Find the target version in history
    const { data: targetVersionData, error: versionError } = await serviceClient
      .from('version_history')
      .select('*')
      .eq('assessment_id', id)
      .eq('version_number', versionNumber)
      .single();
    
    if (versionError || !targetVersionData) {
      console.error('Error finding version history:', versionError);
      return null;
    }
    
    // Save current version to history
    const { error: historyError } = await serviceClient
      .from('version_history')
      .insert({
        assessment_id: id,
        version_number: currentAssessment.version,
        categories_snapshot: JSON.stringify(currentAssessment.categories)
      });
      
    if (historyError) {
      console.error('Error creating version history entry:', historyError);
      throw historyError;
    }
    
    // Update assessment version
    const { error: updateError } = await serviceClient
      .from('assessments')
      .update({
        version: currentAssessment.version + 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);
      
    if (updateError) {
      console.error('Error updating assessment version:', updateError);
      throw updateError;
    }
    
    // Parse categories from the target version
    // Ensure we properly handle the categories_snapshot which could be a JSON string or already parsed object
    let categoriesFromHistory;
    if (typeof targetVersionData.categories_snapshot === 'string') {
      categoriesFromHistory = JSON.parse(targetVersionData.categories_snapshot);
    } else {
      categoriesFromHistory = targetVersionData.categories_snapshot;
    }
    
    // Restore categories structure
    await saveCategoriesQuestionsAnswers(id, categoriesFromHistory);
    
    // Get the updated assessment
    return await getAssessmentById(id);
  } catch (error) {
    console.error('Error in revertToVersion:', error);
    throw error;
  }
};
